import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle, Archive, Phone, Mail, MapPin, Calendar, Camera, Video,
  User, Key, Copy, RefreshCw, Trash2, Code, ChevronDown,
  ChevronUp, Clock, DollarSign, Users, Eye, AlertCircle, Edit, Bell,
  BellOff, X, Save, Plus, Loader2, Globe, Send
} from "lucide-react";
import AdminAddLeadModal from '@/components/AdminAddLeadModal';
import { leadStatus, badgeStyle } from "@/utils/szStatus";

const AdminLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [leadsPerPage] = useState(10);
  const [showWebhookSettings, setShowWebhookSettings] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [partners, setPartners] = useState([]);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [creatingPartner, setCreatingPartner] = useState(false);
  const [newKeyForPartner, setNewKeyForPartner] = useState(null); // {id, name, api_key} shown once after create/rotate
  const [expandedLead, setExpandedLead] = useState(null);
  const [showApproveMenu, setShowApproveMenu] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [approvingIds, setApprovingIds] = useState(() => new Set());

  const adminEmail = localStorage.getItem('shootzu_admin_email') || 'admin@shootzu.com';
  const authHeaders = () => {
    const token = localStorage.getItem('shootzu_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const isAdmin = localStorage.getItem('shootzu_admin');
    if (!isAdmin) navigate('/szadmin');
    fetchLeads();
    fetchPartners();
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads`,
        { headers: authHeaders() }
      );
      if (response.data.success) {
        setLeads(response.data.leads || []);
      }
    } catch (error) {
      console.error('Fetch leads error:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/webhook-partners`,
        { headers: authHeaders() }
      );
      if (response.data.success) {
        setPartners(response.data.partners || []);
        setWebhookUrl(response.data.webhook_url || '');
      }
    } catch (error) {
      console.error('Fetch webhook partners error:', error);
    }
  };

  const createPartner = async () => {
    const name = newPartnerName.trim();
    if (!name) {
      toast.error('Enter a partner name');
      return;
    }
    try {
      setCreatingPartner(true);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/webhook-partners`,
        { name },
        { headers: authHeaders() }
      );
      if (response.data.success) {
        toast.success('Partner created');
        setNewPartnerName('');
        const p = response.data.partner;
        setNewKeyForPartner({ id: p.id, name: p.name, api_key: p.api_key });
        fetchPartners();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create partner');
    } finally {
      setCreatingPartner(false);
    }
  };

  const rotatePartnerKey = async (partner) => {
    if (!window.confirm(`Rotate the key for "${partner.name}"? Their current key will stop working immediately.`)) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/webhook-partners/${partner.id}/rotate`,
        {},
        { headers: authHeaders() }
      );
      if (response.data.success) {
        toast.success('Key rotated');
        setNewKeyForPartner({ id: partner.id, name: partner.name, api_key: response.data.api_key });
        fetchPartners();
      }
    } catch (error) {
      toast.error('Failed to rotate key');
    }
  };

  const revokePartner = async (partner) => {
    if (!window.confirm(`Revoke "${partner.name}"? Their integration will stop working.`)) return;
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/webhook-partners/${partner.id}`,
        { headers: authHeaders() }
      );
      if (response.data.success) {
        toast.success('Partner revoked');
        fetchPartners();
      }
    } catch (error) {
      toast.error('Failed to revoke partner');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const handleApprove = async (leadId, withNotifications = true) => {
    if (approvingIds.has(leadId)) return; // hard guard against repeat clicks
    setShowApproveMenu(null);
    setApprovingIds(prev => {
      const next = new Set(prev);
      next.add(leadId);
      return next;
    });
    try {
      const action = withNotifications ? 'approve' : 'approve_silent';
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${leadId}`,
        { admin_email: adminEmail, action }
      );
      if (response.data.success) {
        if (response.data.already_approved) {
          toast.info('Lead was already approved - no extra notifications sent');
        } else if (response.data.warning === 'no_coordinates') {
          toast.error(response.data.message, { duration: Infinity });
        } else if (withNotifications) {
          toast.success(`Lead approved - notified ${response.data.notified ?? 0} nearby users`);
        } else {
          toast.success('Lead approved (no notifications sent)');
        }
        fetchLeads();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve lead');
    } finally {
      setApprovingIds(prev => {
        const next = new Set(prev);
        next.delete(leadId);
        return next;
      });
    }
  };

  const handleResendNotifications = async (lead) => {
    if (!window.confirm(`Resend New Lead notifications for ${lead.lead_number}? This emails every eligible crew member nearby.`)) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${lead.id}/resend-notifications`,
        {},
        { headers: authHeaders() }
      );
      if (response.data.success) {
        toast.success(`Notified ${response.data.notified} users (${response.data.emails} emails, ${response.data.push} push)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend notifications');
    }
  };

  const handleArchive = async (leadId) => {
    if (!window.confirm('Archive this lead?')) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${leadId}`,
        { admin_email: adminEmail, action: 'archive' }
      );
      if (response.data.success) {
        toast.success('Lead archived');
        fetchLeads();
      }
    } catch (error) {
      toast.error('Failed to archive lead');
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setEditFormData({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      phone_number: lead.phone_number || '',
      location: lead.location || '',
      date_required: lead.date_required?.split('T')[0] || '',
      service_required: lead.service_required || [],
      brief: lead.brief || '',
      budget: lead.budget || '',
      max_reveals: lead.max_reveals || 6
    });
  };

  const handleSaveEdit = async () => {
    if (!editingLead) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/${editingLead.id}`,
        { admin_email: adminEmail, updates: editFormData }
      );
      if (response.data.success) {
        toast.success('Lead updated successfully');
        setEditingLead(null);
        fetchLeads();
      }
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const toggleService = (service) => {
    setEditFormData(prev => ({
      ...prev,
      service_required: prev.service_required.includes(service)
        ? prev.service_required.filter(s => s !== service)
        : [...prev.service_required, service]
    }));
  };

  const filteredLeads = leads.filter(lead => {
    if (filter === 'all') return true;
    return lead.status === filter;
  });

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const pendingCount = leads.filter(l => l.status === 'pending').length;
  const approvedCount = leads.filter(l => l.status === 'approved').length;
  const archivedCount = leads.filter(l => l.status === 'archived').length;
  const totalReveals = leads.reduce((sum, l) => sum + (l.reveals_count || 0), 0);

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch { return dateStr; }
  };

  // Source icon tile config: known sources get a styled icon; default → Globe.
  const getSourceConfig = (source) => {
    const s = (source || 'direct').toLowerCase();
    if (s === 'partner' || s === 'webhook') return { icon: Users, bg: '#EDE4F4', fg: '#78287C' };
    if (s === 'referral') return { icon: Send, bg: '#CCFBF1', fg: '#0F766E' };
    return { icon: Globe, bg: '#DBEAFE', fg: '#2563EB' };
  };

  const stats = [
    { label: 'Total Leads', value: leads.length, dot: '#572B7C' },
    { label: 'Pending', value: pendingCount, dot: '#B45309' },
    { label: 'Approved', value: approvedCount, dot: '#15803D' },
    { label: 'Total Reveals', value: totalReveals, dot: '#28C0D0' },
  ];

  const tabs = [
    { key: 'pending', label: 'Pending', count: pendingCount },
    { key: 'approved', label: 'Approved', count: approvedCount },
    { key: 'archived', label: 'Archived', count: archivedCount },
    { key: 'all', label: 'All', count: leads.length },
  ];

  const embedCode = `<iframe src="${window.location.origin}/getquotes?embed=true" width="100%" height="800"></iframe>`;

  return (
    <div className="szscroll">
      {/* Header */}
      <header className="sz-header">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="sz-h1 m-0">Lead Management</h1>
              <div className="text-[12.5px] text-[#8A90A2] mt-1">Inbound enquiries &amp; partner leads</div>
            </div>
            {pendingCount > 0 && (
              <span
                className="text-[11.5px] font-bold px-2.5 py-1 rounded-full"
                style={{ fontFamily: 'Poppins,sans-serif', color: '#B45309', background: '#FEF3C7' }}
              >
                {pendingCount} pending
              </span>
            )}
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="sz-btn sz-btn-primary"
          >
            <Plus className="w-4 h-4" />
            Add Lead
          </button>
        </div>
      </header>

      <main style={{ padding: '22px 30px 56px', maxWidth: '1080px' }}>
        {/* Stats Grid */}
        <div className="grid gap-[13px]" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))' }}>
          {stats.map((s) => (
            <div key={s.label} className="sz-card p-4">
              <div className="flex items-center gap-2.5">
                <span className="w-[9px] h-[9px] rounded-[3px] shrink-0" style={{ background: s.dot }} />
                <span className="text-xs font-semibold text-[#8A90A2]">{s.label}</span>
              </div>
              <div className="sz-stat-num mt-2 text-[#0C1120]">{s.value.toLocaleString()}</div>
            </div>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1.5 mt-[18px] flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`sz-pill ${filter === tab.key ? 'sz-active' : ''}`}
            >
              {tab.label}
              <span className="sz-pill-count">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Third-Party Integration */}
        <div className="sz-card mt-4 overflow-hidden">
          <button
            onClick={() => setShowWebhookSettings(!showWebhookSettings)}
            className="w-full px-[18px] py-[15px] flex items-center justify-between text-left transition-colors hover:bg-[#FAFBFC]"
          >
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-[#78287C]" />
              <span className="font-semibold text-[#2B3142] text-[13px]">Third-Party Integration</span>
            </div>
            {showWebhookSettings
              ? <ChevronUp className="w-4 h-4 text-[#A8AEBC]" />
              : <ChevronDown className="w-4 h-4 text-[#A8AEBC]" />}
          </button>

          {showWebhookSettings && (
            <div className="px-[18px] pb-[18px] border-t border-[#F1F3F7] pt-4 space-y-4 sz-fade">
              {/* Webhook URL */}
              <div className="bg-[#FAFBFC] rounded-[12px] p-3 border border-[#F1F3F7]">
                <p className="sz-overline text-[#8A90A2] mb-2">Webhook URL</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-[9px] border border-[#E6E9EF] text-xs sz-mono text-[#2B3142] break-all">
                    {webhookUrl || 'Loading...'}
                  </code>
                  <button className="sz-btn sz-btn-outline px-3" onClick={() => copyToClipboard(webhookUrl, 'URL')}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Partner API Keys */}
              <div className="bg-[#FAFBFC] rounded-[12px] p-3 border border-[#F1F3F7]">
                <div className="flex items-center justify-between mb-2">
                  <p className="sz-overline text-[#8A90A2]">Partner API Keys</p>
                  <span className="text-[10px] text-[#A8AEBC]">Each website gets its own key</span>
                </div>

                {/* One-time reveal of a freshly created/rotated key */}
                {newKeyForPartner && (
                  <div className="mb-3 bg-[#EDE4F4] border border-[#D9C7E4] rounded-[12px] p-3">
                    <p className="text-xs font-semibold text-[#78287C] mb-1">
                      New key for "{newKeyForPartner.name}" — copy it now, it won't be shown again in full.
                    </p>
                    <div className="flex gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded-[9px] border border-[#D9C7E4] text-xs sz-mono text-[#2B3142] break-all">
                        {newKeyForPartner.api_key}
                      </code>
                      <button className="sz-btn sz-btn-outline px-3" onClick={() => copyToClipboard(newKeyForPartner.api_key, 'Key')}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                      <button className="sz-btn sz-btn-outline px-3 text-[#8A90A2]" onClick={() => setNewKeyForPartner(null)}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Add partner */}
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newPartnerName}
                    onChange={(e) => setNewPartnerName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createPartner(); }}
                    placeholder="New partner name (e.g. Acme Photography)"
                    className="sz-input flex-1"
                  />
                  <button onClick={createPartner} disabled={creatingPartner} className="sz-btn sz-btn-primary">
                    {creatingPartner ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Add
                  </button>
                </div>

                {/* Partner list */}
                {partners.length === 0 ? (
                  <p className="text-xs text-[#A8AEBC] py-2">No partners yet. Add one to issue an API key.</p>
                ) : (
                  <div className="space-y-2">
                    {partners.map((p) => (
                      <div key={p.id} className={`bg-white rounded-[10px] border px-3 py-2 ${p.active ? 'border-[#E6E9EF]' : 'border-[#E6E9EF] opacity-60'}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-[#2B3142] truncate">{p.name}</span>
                              {!p.active && (
                                <span className="sz-badge sz-nodot text-[10px]" style={badgeStyle({ fg: '#B91C1C', bg: '#FEE2E2' })}>revoked</span>
                              )}
                            </div>
                            <p className="text-[10px] text-[#A8AEBC] sz-mono">source: {p.source}</p>
                          </div>
                          <div className="flex items-center gap-3 text-[10px] text-[#8A90A2]">
                            <span>{p.lead_count || 0} leads</span>
                            <span>{p.last_used_at ? `used ${new Date(p.last_used_at).toLocaleDateString()}` : 'never used'}</span>
                          </div>
                          <div className="flex gap-1.5">
                            <button
                              onClick={() => rotatePartnerKey(p)}
                              disabled={!p.active}
                              className="sz-btn sz-btn-outline text-[11px] py-1.5 px-2.5"
                              style={{ color: '#B45309', borderColor: '#FDE68A' }}
                            >
                              <RefreshCw className="w-3 h-3" />
                              Rotate
                            </button>
                            {p.active && (
                              <button
                                onClick={() => revokePartner(p)}
                                className="sz-btn sz-btn-outline text-[11px] py-1.5 px-2.5"
                                style={{ color: '#B91C1C', borderColor: '#FECACA' }}
                              >
                                <Trash2 className="w-3 h-3" />
                                Revoke
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Embed Form */}
              <div className="bg-[#FAFBFC] rounded-[12px] p-3 border border-[#F1F3F7]">
                <p className="sz-overline text-[#8A90A2] mb-2">Embed Form</p>
                <div className="flex gap-2">
                  <code className="flex-1 bg-white px-3 py-2 rounded-[9px] border border-[#E6E9EF] text-[10px] sz-mono text-[#8A90A2] break-all">
                    {embedCode}
                  </code>
                  <button className="sz-btn sz-btn-outline px-3" onClick={() => copyToClipboard(
                    `<iframe src="${window.location.origin}/getquotes?embed=true" width="100%" height="800" frameborder="0"></iframe>`,
                    'Code'
                  )}>
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Leads List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-[#E6E9EF] border-t-[#78287C]"></div>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="sz-card mt-4 p-12 text-center">
            <div className="w-16 h-16 bg-[#EEF0F4] rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-[#A8AEBC]" />
            </div>
            <p className="text-[#2B3142] font-semibold">No {filter !== 'all' ? filter : ''} leads found</p>
            <p className="text-sm text-[#8A90A2] mt-1">Leads will appear here when submitted</p>
          </div>
        ) : (
          <div className="flex flex-col gap-[11px] mt-4">
            {currentLeads.map((lead) => {
              const st = leadStatus(lead.status);
              const src = getSourceConfig(lead.source);
              const SrcIcon = src.icon;
              const isExpanded = expandedLead === lead.id;
              const sourceLabel = lead.source && lead.source !== 'direct' ? lead.source : 'Direct';

              return (
                <div
                  key={lead.id}
                  className={`sz-card sz-fade ${
                    showApproveMenu === lead.id
                      ? 'overflow-visible relative z-40'  /* open menu: don't clip it, lift above sibling cards + the click-outside overlay */
                      : 'overflow-hidden'
                  }`}
                >
                  {/* Collapsed header row — always visible */}
                  <div
                    onClick={() => setExpandedLead(isExpanded ? null : lead.id)}
                    className="flex items-center gap-[14px] px-[18px] py-[15px] cursor-pointer transition-colors hover:bg-[#FAFBFC]"
                  >
                    {/* Source icon tile */}
                    <div
                      className="w-10 h-10 rounded-[11px] flex items-center justify-center shrink-0"
                      style={{ background: src.bg, color: src.fg }}
                    >
                      <SrcIcon className="w-[19px] h-[19px]" />
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[14px] font-semibold text-[#0C1120]">
                          {lead.first_name} {lead.last_name}
                        </span>
                        <span className="text-[10px] font-bold text-[#A8AEBC]" style={{ fontFamily: 'Poppins,sans-serif' }}>
                          {lead.lead_number}
                        </span>
                        <span className="sz-badge sz-nodot text-[10px]" style={{ ...badgeStyle(st), fontFamily: 'Poppins,sans-serif' }}>
                          {st.label}
                        </span>
                        <span
                          className="text-[10px] font-semibold px-2 py-[3px] rounded-full"
                          style={{ fontFamily: 'Poppins,sans-serif', color: '#475569', background: '#EEF0F4' }}
                        >
                          {sourceLabel}
                        </span>
                      </div>
                      <div className="text-xs text-[#8A90A2] mt-1 truncate">
                        {(lead.service_required || []).join(', ') || 'Service TBD'}
                        {' · '}
                        {lead.location?.split(',')[0] || 'Location TBD'}
                        {' · needed '}
                        {formatDate(lead.date_required)}
                      </div>
                    </div>

                    {/* Reveals */}
                    <div className="text-right shrink-0">
                      <div className="text-[11px] text-[#8A90A2]">Reveals</div>
                      <div className="text-[14px] font-bold text-[#0C1120]" style={{ fontFamily: 'Poppins,sans-serif' }}>
                        {lead.reveals_count}/{lead.max_reveals}
                      </div>
                    </div>

                    {/* Chevron */}
                    <ChevronDown
                      className="w-[18px] h-[18px] text-[#A8AEBC] shrink-0 transition-transform duration-200"
                      style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}
                    />
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-[18px] pb-[18px] border-t border-[#F1F3F7]">
                      {/* Field grid */}
                      <div className="grid gap-x-5 gap-y-[14px] pt-[15px]" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))' }}>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Phone</div>
                          <a href={`tel:${lead.phone_number}`} className="text-[13px] font-medium text-[#0C1120] hover:text-[#78287C] inline-flex items-center gap-1.5">
                            <Phone className="w-3.5 h-3.5" />
                            {lead.phone_number}
                          </a>
                        </div>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Email</div>
                          <a href={`mailto:${lead.email}`} className="text-[13px] font-medium text-[#0C1120] hover:text-[#78287C] inline-flex items-center gap-1.5 break-all">
                            <Mail className="w-3.5 h-3.5 shrink-0" />
                            {lead.email}
                          </a>
                        </div>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Location</div>
                          <div className="text-[13px] font-medium text-[#0C1120] inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 shrink-0" />
                            {lead.location || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Budget</div>
                          <div className="text-[13px] font-medium text-[#0C1120] inline-flex items-center gap-1.5">
                            <DollarSign className="w-3.5 h-3.5 shrink-0" />
                            {lead.budget || '—'}
                          </div>
                        </div>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Date Required</div>
                          <div className="text-[13px] font-medium text-[#0C1120] inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5 shrink-0" />
                            {formatDate(lead.date_required)}
                          </div>
                        </div>
                        <div>
                          <div className="sz-overline text-[#8A90A2] mb-[3px]">Service</div>
                          <div className="flex flex-wrap gap-1.5">
                            {(lead.service_required || []).length === 0 ? (
                              <span className="text-[13px] font-medium text-[#0C1120]">—</span>
                            ) : (
                              (lead.service_required || []).map((s) => (
                                <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#EDE4F4] text-[#78287C] text-[11px] font-medium">
                                  {s === 'photography' ? <Camera className="w-3 h-3" /> : <Video className="w-3 h-3" />}
                                  {s}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Project brief */}
                      <div className="mt-[14px]">
                        <div className="sz-overline text-[#8A90A2] mb-1">Project Brief</div>
                        <p className="text-[12.5px] leading-[1.55] text-[#2B3142] m-0 whitespace-pre-wrap">
                          {lead.brief}
                        </p>
                      </div>

                      {/* Meta */}
                      <div className="mt-[12px] flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#A8AEBC]">
                        <span>Submitted: {formatDate(lead.created_at)}</span>
                        {lead.approved_at && <span>Approved: {formatDate(lead.approved_at)}</span>}
                        {!lead.phone_verified && <span className="text-[#B45309]">⚠ Phone not verified</span>}
                        {(!lead.location_lat || !lead.location_lon) && (
                          <span className="text-[#B91C1C]">⚠ No coordinates - crew notifications will not send</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        {lead.status === 'pending' && (
                          <div className="relative" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setShowApproveMenu(showApproveMenu === lead.id ? null : lead.id)}
                              disabled={approvingIds.has(lead.id)}
                              className="sz-btn"
                              style={{ background: '#15803D', color: '#fff' }}
                            >
                              {approvingIds.has(lead.id) ? (
                                <>
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  Approving...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Approve &amp; Notify
                                  <ChevronDown className="w-3 h-3" />
                                </>
                              )}
                            </button>
                            {showApproveMenu === lead.id && !approvingIds.has(lead.id) && (
                              <div className="sz-menu absolute left-0 mt-1 w-52 py-1 z-30">
                                <button
                                  onClick={() => handleApprove(lead.id, true)}
                                  disabled={approvingIds.has(lead.id)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-[#FAFBFC] flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <Bell className="w-4 h-4 text-[#15803D]" />
                                  <div>
                                    <p className="font-semibold text-[#2B3142]">Approve + Notify</p>
                                    <p className="text-xs text-[#8A90A2]">Send to nearby users</p>
                                  </div>
                                </button>
                                <button
                                  onClick={() => handleApprove(lead.id, false)}
                                  disabled={approvingIds.has(lead.id)}
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-[#FAFBFC] flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                  <BellOff className="w-4 h-4 text-[#8A90A2]" />
                                  <div>
                                    <p className="font-semibold text-[#2B3142]">Approve Silent</p>
                                    <p className="text-xs text-[#8A90A2]">No notifications</p>
                                  </div>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(lead); }}
                          className="sz-btn sz-btn-outline"
                        >
                          <Edit className="w-3.5 h-3.5" />
                          Edit
                        </button>
                        {lead.status === 'approved' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResendNotifications(lead); }}
                            className="sz-btn sz-btn-outline"
                          >
                            <Bell className="w-3.5 h-3.5" />
                            Resend Notifications
                          </button>
                        )}
                        {lead.status !== 'archived' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleArchive(lead.id); }}
                            className="sz-btn sz-btn-outline text-[#8A90A2]"
                          >
                            <Archive className="w-3.5 h-3.5" />
                            Archive
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sz-card p-3">
            <p className="text-xs text-[#8A90A2]">
              {indexOfFirstLead + 1}-{Math.min(indexOfLastLead, filteredLeads.length)} of {filteredLeads.length}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="sz-btn sz-btn-outline text-xs py-1.5"
              >
                Previous
              </button>
              <div className="hidden sm:flex items-center gap-1">
                {[...Array(totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (pageNum === 1 || pageNum === totalPages || Math.abs(pageNum - currentPage) <= 1) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`w-8 h-8 rounded-[9px] text-xs font-medium ${
                          currentPage === pageNum
                            ? 'bg-[#0C1120] text-white'
                            : 'text-[#8A90A2] hover:bg-[#EEF0F4]'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="text-[#A8AEBC]">...</span>;
                  }
                  return null;
                })}
              </div>
              <span className="sm:hidden text-xs text-[#8A90A2] px-2">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="sz-btn sz-btn-outline text-xs py-1.5"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Edit Lead Modal */}
      {editingLead && (
        <div className="sz-modal-backdrop" onClick={() => setEditingLead(null)}>
          <div className="sz-modal-card w-full max-w-2xl szscroll overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sz-modal-header-grad rounded-t-[19px] px-5 py-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <h2 className="text-lg font-bold text-white m-0" style={{ fontFamily: 'Poppins,sans-serif' }}>Edit Lead</h2>
                <p className="text-xs text-white/80 mt-0.5">{editingLead.lead_number}</p>
              </div>
              <button
                onClick={() => setEditingLead(null)}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Name */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">First Name</label>
                  <input
                    type="text"
                    value={editFormData.first_name}
                    onChange={(e) => setEditFormData({...editFormData, first_name: e.target.value})}
                    className="sz-input"
                  />
                </div>
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">Last Name</label>
                  <input
                    type="text"
                    value={editFormData.last_name}
                    onChange={(e) => setEditFormData({...editFormData, last_name: e.target.value})}
                    className="sz-input"
                  />
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">Email</label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                    className="sz-input"
                  />
                </div>
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={editFormData.phone_number}
                    onChange={(e) => setEditFormData({...editFormData, phone_number: e.target.value})}
                    className="sz-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Location</label>
                <input
                  type="text"
                  value={editFormData.location}
                  onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                  className="sz-input"
                />
              </div>

              {/* Date & Budget */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">Date Required</label>
                  <input
                    type="date"
                    value={editFormData.date_required}
                    onChange={(e) => setEditFormData({...editFormData, date_required: e.target.value})}
                    className="sz-input"
                  />
                </div>
                <div>
                  <label className="block sz-overline text-[#8A90A2] mb-1.5">Budget</label>
                  <input
                    type="text"
                    value={editFormData.budget}
                    onChange={(e) => setEditFormData({...editFormData, budget: e.target.value})}
                    className="sz-input"
                    placeholder="e.g. $500-$1000"
                  />
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-2">Services Required</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => toggleService('photography')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-[11px] text-sm font-medium transition-all ${
                      editFormData.service_required?.includes('photography')
                        ? 'bg-[#572B7C] text-white'
                        : 'bg-[#EEF0F4] text-[#8A90A2] hover:bg-[#E0E3EB]'
                    }`}
                  >
                    <Camera className="w-4 h-4" />
                    Photography
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleService('videography')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-[11px] text-sm font-medium transition-all ${
                      editFormData.service_required?.includes('videography')
                        ? 'bg-[#572B7C] text-white'
                        : 'bg-[#EEF0F4] text-[#8A90A2] hover:bg-[#E0E3EB]'
                    }`}
                  >
                    <Video className="w-4 h-4" />
                    Videography
                  </button>
                </div>
              </div>

              {/* Max Reveals */}
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Max Reveals</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editFormData.max_reveals}
                  onChange={(e) => setEditFormData({...editFormData, max_reveals: parseInt(e.target.value) || 6})}
                  className="sz-input w-24"
                />
              </div>

              {/* Brief */}
              <div>
                <label className="block sz-overline text-[#8A90A2] mb-1.5">Project Brief</label>
                <textarea
                  value={editFormData.brief}
                  onChange={(e) => setEditFormData({...editFormData, brief: e.target.value})}
                  rows={5}
                  className="sz-input resize-none"
                />
              </div>
            </div>

            <div className="bg-[#FAFBFC] border-t border-[#F1F3F7] px-5 py-3 flex justify-end gap-2 rounded-b-[19px]">
              <button
                onClick={() => setEditingLead(null)}
                className="sz-btn sz-btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="sz-btn sz-btn-primary"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close approve menu */}
      {showApproveMenu && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setShowApproveMenu(null)}
        />
      )}

      {/* Add Lead Modal */}
      <AdminAddLeadModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdded={fetchLeads}
      />
    </div>
  );
};

export default AdminLeads;
